import * as React from 'react';
import { stripUaePrefix } from '../../lib/phone';
import { cn } from '../../lib/utils';
import type { InputProps } from './input';

const UAE_PREFIX = '+971';

export interface PhoneInputProps extends Omit<InputProps, 'type' | 'value'> {
  value?: string | null;
}

/**
 * UAE mobile number input: renders a fixed, non-editable "+971" prefix and
 * restricts typing to the 9-digit subscriber number. The value exposed to
 * the parent form (via onChange) is always the full canonical
 * "+971XXXXXXXXX" string, or "" when empty — regardless of what format the
 * initial `value` was stored in (accepts legacy +971.../971.../0... values
 * for pre-fill so existing profiles still display correctly).
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ onChange, value, hasError, className, ...props }, ref) => {
    const subscriber = stripUaePrefix(typeof value === 'string' ? value : '');

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
        e.target.value = digits ? `${UAE_PREFIX}${digits}` : '';
        onChange?.(e);
      },
      [onChange],
    );

    return (
      <div
        className={cn(
          'flex items-center rounded-xl border bg-white transition-all focus-within:ring-2',
          hasError
            ? 'border-error-400 focus-within:border-error-500 focus-within:ring-error-100'
            : 'border-gray-300 hover:border-gray-400 focus-within:border-brand-400 focus-within:ring-brand-100',
          className,
        )}
      >
        <span className="pl-3.5 py-2.5 text-sm text-gray-500 select-none">{UAE_PREFIX}</span>
        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          maxLength={9}
          value={subscriber}
          onChange={handleChange}
          className="w-full rounded-r-xl bg-transparent py-2.5 pr-3.5 pl-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
          {...props}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';
